<template>
  <div class="material-check-page frame-page user-manage-page">
    <div class="page-intro">
      <p>维护登录账号：用户名、密码、角色。角色权限在「权限设置」中配置。</p>
    </div>

    <section class="panel">
      <div class="panel-title panel-title-actions">
        <div class="panel-title-left">
          <span class="step">1</span>
          <span>用户列表</span>
          <span v-if="users.length" class="tag tag-done">{{ users.length }} 人</span>
          <span v-else class="tag tag-muted">待添加</span>
        </div>
        <div class="panel-title-right">
          <Button size="s" color="primary" @click="openCreate">新增用户</Button>
        </div>
      </div>

      <div v-if="loading" class="empty-hint">加载中…</div>
      <div v-else-if="!users.length" class="empty-hint">还没有用户。</div>
      <div v-else class="table-wrap">
        <table class="data-table">
          <thead>
            <tr>
              <th>用户名</th>
              <th>角色</th>
              <th>状态</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in users" :key="item.id" :class="{ 'is-disabled': !item.isActive }">
              <td>{{ item.username }}</td>
              <td>{{ item.roleName || '—' }}</td>
              <td>
                <span class="status-tag" :class="item.isActive ? 'is-on' : 'is-off'">
                  {{ item.isActive ? '已启用' : '已禁用' }}
                </span>
              </td>
              <td class="col-actions">
                <Button size="s" @click="openEdit(item)">编辑</Button>
                <Button
                  size="s"
                  :disabled="item.id === currentUserId && item.isActive"
                  @click="toggleActive(item)"
                >{{ item.isActive ? '禁用' : '启用' }}</Button>
                <Button
                  size="s"
                  class="drop-btn-clear"
                  :disabled="item.id === currentUserId"
                  @click="removeUser(item)"
                >删除</Button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <Modal
      v-model="editorVisible"
      :title="editorTitle"
      :middle="true"
      hasDivider
      :closeOnMask="false"
    >
      <div class="form-grid">
        <div class="autofill-trap" aria-hidden="true">
          <input type="text" name="username" autocomplete="username" tabindex="-1" />
          <input type="password" name="password" autocomplete="current-password" tabindex="-1" />
        </div>
        <label class="form-item">
          <span>用户名<span class="req">*</span></span>
          <Input
            v-model="form.username"
            name="sys-new-username"
            autocomplete="off"
            placeholder="请输入用户名，支持中文"
          />
        </label>
        <label class="form-item">
          <span>{{ editingId ? '密码（不修改请留空）' : '密码' }}<span v-if="!editingId" class="req">*</span></span>
          <Input
            v-model="form.password"
            type="password"
            name="sys-new-password"
            autocomplete="new-password"
            :placeholder="editingId ? '不修改请留空' : '请输入密码'"
          />
        </label>
        <label class="form-item">
          <span>角色</span>
          <Select v-model="form.roleId" :datas="roleOptions" placeholder="选择角色" />
        </label>
      </div>
      <template #footer>
        <Button @click="editorVisible = false">取消</Button>
        <Button color="primary" :loading="saving" @click="saveUser">保存</Button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useStore } from 'vuex';
import { message } from 'heyui';
import Request from '@common/request';

const store = useStore();
const loading = ref(false);
const saving = ref(false);
const editorVisible = ref(false);
const editingId = ref(null);
const users = ref([]);
const roles = ref([]);

const form = reactive({
  username: '',
  password: '',
  roleId: 2,
});

const currentUserId = computed(() => Number(store.state.user?.id || 0));
const editorTitle = computed(() => (editingId.value ? '编辑用户' : '新增用户'));
const roleOptions = computed(() =>
  roles.value.map((role) => ({ key: role.id, title: role.name }))
);

async function loadRoles() {
  const resp = await Request.Management.roles();
  if (resp.ok) {
    roles.value = resp.body || [];
  }
}

async function loadUsers() {
  loading.value = true;
  try {
    const resp = await Request.Management.sysUsers();
    if (!resp.ok) {
      message.error(resp._msg || '加载用户失败');
      return;
    }
    users.value = resp.body || [];
  } finally {
    loading.value = false;
  }
}

function resetForm() {
  form.username = '';
  form.password = '';
  form.roleId = roles.value.some((role) => Number(role.id) === 2) ? 2 : (roles.value[0]?.id || 1);
}

function openCreate() {
  editingId.value = null;
  resetForm();
  editorVisible.value = true;
  nextTick(() => {
    resetForm();
    setTimeout(resetForm, 80);
  });
}

function openEdit(item) {
  editingId.value = item.id;
  form.username = item.username || '';
  form.password = '';
  form.roleId = item.roleId;
  editorVisible.value = true;
}

function payload() {
  return {
    username: String(form.username || '').trim(),
    password: String(form.password || ''),
    roleId: Number(form.roleId),
  };
}

async function saveUser() {
  const data = payload();
  if (!data.username) {
    message.error('请填写用户名');
    return;
  }
  if (!editingId.value && !data.password) {
    message.error('请填写密码');
    return;
  }
  if (!data.roleId) {
    message.error('请选择角色');
    return;
  }

  saving.value = true;
  try {
    const resp = editingId.value
      ? await Request.Management.updateSysUser(editingId.value, data)
      : await Request.Management.createSysUser(data);
    if (!resp.ok) {
      message.error(resp._msg || '保存失败');
      return;
    }
    message.success(editingId.value ? '已更新' : '已新增');
    editorVisible.value = false;
    await loadUsers();
  } finally {
    saving.value = false;
  }
}

async function toggleActive(item) {
  const nextActive = !item.isActive;
  if (!nextActive && item.id === currentUserId.value) {
    message.error('不能禁用当前登录账号');
    return;
  }
  const action = nextActive ? '启用' : '禁用';
  if (!window.confirm(`确定${action}用户「${item.username}」吗？`)) {
    return;
  }
  const resp = await Request.Management.setSysUserActive(item.id, nextActive);
  if (!resp.ok) {
    message.error(resp._msg || `${action}失败`);
    return;
  }
  message.success(`已${action}`);
  await loadUsers();
}

async function removeUser(item) {
  if (item.id === currentUserId.value) {
    message.error('不能删除当前登录账号');
    return;
  }
  if (!window.confirm(`确定删除用户「${item.username}」吗？删除后该账号无法登录。`)) {
    return;
  }
  const resp = await Request.Management.deleteSysUser(item.id);
  if (!resp.ok) {
    message.error(resp._msg || '删除失败');
    return;
  }
  message.success('已删除');
  await loadUsers();
}

onMounted(async () => {
  await loadRoles();
  await loadUsers();
});
</script>

<style lang="less" scoped>
@import '@/views/material-check/material-check.less';

.empty-hint {
  padding: 28px 0;
  text-align: center;
  color: @app-muted;
  font-size: 13px;
}

.table-wrap {
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  th,
  td {
    padding: 9px 10px;
    text-align: left;
    border-bottom: 1px solid #eef1f5;
    white-space: nowrap;
  }

  th {
    color: @app-muted;
    font-weight: 500;
  }

  td {
    color: @app-text;
  }

  .is-disabled td {
    color: @app-muted;
  }
}

.status-tag {
  display: inline-block;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 12px;
}

.status-tag.is-on {
  color: #2f7d4a;
  background: #e8f6ee;
}

.status-tag.is-off {
  color: #8a8f99;
  background: #f2f3f5;
}

.col-actions {
  text-align: right;

  .h-btn + .h-btn {
    margin-left: 8px;
  }
}

.form-grid {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  color: @app-text;
}

.req {
  margin-left: 2px;
  color: #d94444;
}

.autofill-trap {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.drop-btn-clear {
  color: #c45656;
}
</style>
